import type { QueryClient } from '@tanstack/react-query';
import { customerAgentKeys } from '@/features/customer-agent/hooks/customer-agent-keys';
import type { ConversationView } from '../types/conversations.types';

/* ==========================================================================
   Chaves de cache do Atendimento, por estabelecimento

   Ficam fora dos hooks para a conexão de pedidos (useOrdersSignalR)
   revalidar as conversas sem carregar a tela, como whatsapp-keys.ts.
   ========================================================================== */

export const conversationsKeys = {
  root: (tenantId: string) => ['conversations', tenantId] as const,
  summary: (tenantId: string) => [...conversationsKeys.root(tenantId), 'summary'] as const,
  lists: (tenantId: string) => [...conversationsKeys.root(tenantId), 'list'] as const,
  list: (tenantId: string, view: ConversationView) => [...conversationsKeys.lists(tenantId), view] as const,
  detail: (tenantId: string, conversationId: string) =>
    [...conversationsKeys.root(tenantId), 'detail', conversationId] as const,
  messages: (tenantId: string, conversationId: string) =>
    [...conversationsKeys.root(tenantId), 'messages', conversationId] as const,
  byOrder: (tenantId: string, orderId: string) => [...conversationsKeys.root(tenantId), 'by-order', orderId] as const,
};

/** O estado do atendente, na fábrica da página Atendente IA (`customer-agent-settings`, D24). */
export const customerAgentStatusKeys = {
  status: customerAgentKeys.status,
};

/**
 * `conversation.updated`, `conversation.assigned` e `conversation.message_added`
 * são campainha: o painel relê a lista, o resumo, a conversa aberta e as
 * mensagens pelo REST, e nunca lê o estado do próprio evento.
 */
export function invalidateConversations(queryClient: QueryClient, tenantId: string): void {
  if (!tenantId) return;
  void queryClient.invalidateQueries({ queryKey: conversationsKeys.root(tenantId) });
}

/** `customer_agent.status_changed`: a faixa de estado e o cabeçalho do Atendimento releem o estado. */
export function invalidateCustomerAgentStatus(queryClient: QueryClient, tenantId: string): void {
  if (!tenantId) return;
  void queryClient.invalidateQueries({ queryKey: customerAgentStatusKeys.status(tenantId) });
}

/** No máximo uma revalidação das conversas por segundo (loja com muitas conversas da IA ao mesmo tempo). */
export const CONVERSATIONS_INVALIDATION_WINDOW_MS = 1_000;

export interface ThrottledInvalidation {
  /** Pede a revalidação: a primeira abre a janela, as seguintes dentro dela se juntam a ela. */
  request: () => void;
  /** Revalida agora e descarta a pendente (reconexão). */
  flush: () => void;
  /** Descarta a pendente (a conexão foi encerrada). */
  cancel: () => void;
}

/**
 * Junta as campainhas de uma janela numa revalidação só, no fim da janela: 30
 * eventos em um segundo viram uma releitura. A primeira campainha espera no
 * máximo a janela, e a seguinte, depois da releitura, abre outra.
 */
export function createThrottledInvalidation(
  invalidate: () => void,
  windowMs = CONVERSATIONS_INVALIDATION_WINDOW_MS,
): ThrottledInvalidation {
  let timer: ReturnType<typeof setTimeout> | null = null;

  const cancel = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };

  return {
    request: () => {
      if (timer !== null) return;
      timer = setTimeout(() => {
        timer = null;
        invalidate();
      }, windowMs);
    },
    flush: () => {
      cancel();
      invalidate();
    },
    cancel,
  };
}
